import { useCallback, useRef, useState } from "react";

/**
 * Undo/redo history stack for JSON-serializable state.
 * Returns { state, setState, commit, undo, redo, canUndo, canRedo, reset }.
 *
 * Usage:
 *   const h = useHistory(initialLayout);
 *   h.setState(next);      // update WITHOUT pushing to history (e.g., during drag)
 *   h.commit();            // snapshot current state to history (e.g., on drag end)
 *   h.commit(nextState);   // set state AND snapshot in one call
 *   h.undo() / h.redo();
 */
export function useHistory(initial, { limit = 50 } = {}) {
  const [state, setState] = useState(initial);
  const [history, setHistory] = useState([JSON.parse(JSON.stringify(initial))]);
  const [idx, setIdx] = useState(0);

  const commit = useCallback((next) => {
    setHistory((h) => {
      const trimmed = h.slice(0, idx + 1);
      const snap = JSON.parse(JSON.stringify(next !== undefined ? next : state));
      const arr = [...trimmed, snap];
      const capped = arr.length > limit ? arr.slice(arr.length - limit) : arr;
      setIdx(capped.length - 1);
      return capped;
    });
    if (next !== undefined) setState(next);
  }, [idx, state, limit]);

  const undo = useCallback(() => {
    if (idx <= 0) return;
    const i = idx - 1;
    setIdx(i);
    setState(JSON.parse(JSON.stringify(history[i])));
  }, [history, idx]);

  const redo = useCallback(() => {
    if (idx >= history.length - 1) return;
    const i = idx + 1;
    setIdx(i);
    setState(JSON.parse(JSON.stringify(history[i])));
  }, [history, idx]);

  const reset = useCallback((next) => {
    setState(next);
    setHistory([JSON.parse(JSON.stringify(next))]);
    setIdx(0);
  }, []);

  return {
    state, setState, commit, undo, redo, reset,
    canUndo: idx > 0,
    canRedo: idx < history.length - 1,
  };
}
